import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Icon, Textarea, FormControl, FormLabel,
  Divider, SimpleGrid, Select, Input, Badge, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2, FiClock } from 'react-icons/fi';
import { faenasApi, Faena, UPLOADS_BASE } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  faenaId: number;
  onCambio?: () => void;
}

const ESTATUS = ['Pendiente', 'Programada', 'En proceso', 'Finalizada', 'Cancelada'];

const ModalFaenaAvances: React.FC<Props> = ({ isOpen, onClose, faenaId, onCambio }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { isOpen: isOpenDel, onOpen: onOpenDel, onClose: onCloseDel } = useDisclosure();

  const [faena, setFaena] = useState<Faena | null>(null);
  const [cargando, setCargando] = useState(false);

  const [estatusNuevo, setEstatusNuevo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [responsable, setResponsable] = useState('');
  const [guardandoAvance, setGuardandoAvance] = useState(false);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [momento, setMomento] = useState('ANTES');
  const [comentario, setComentario] = useState('');
  const [subiendo, setSubiendo] = useState(false);

  const [archivoAEliminar, setArchivoAEliminar] = useState<any>(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const d = await faenasApi.get(faenaId);
      setFaena(d);
    } catch {
      setFaena(null);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (isOpen && faenaId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, faenaId]);

  const handleAgregarAvance = async () => {
    if (!descripcion) {
      toast({ title: 'Descripción requerida', status: 'warning', duration: 3000 });
      return;
    }
    setGuardandoAvance(true);
    try {
      await faenasApi.addAvance(faenaId, { estatusNuevo: estatusNuevo || undefined, descripcion, responsable });
      toast({ title: 'Avance registrado', status: 'success', duration: 2500 });
      setEstatusNuevo(''); setDescripcion(''); setResponsable('');
      await cargar();
      onCambio?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 4000 });
    } finally {
      setGuardandoAvance(false);
    }
  };

  const handleSubirArchivo = async () => {
    if (!archivo) {
      toast({ title: 'Selecciona un archivo', status: 'warning', duration: 3000 });
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('tipoArchivo', archivo.type.startsWith('image/') ? 'foto' : 'documento');
      fd.append('momento', momento);
      fd.append('comentario', comentario);
      await faenasApi.addArchivo(faenaId, fd);
      toast({ title: 'Evidencia subida', status: 'success', duration: 2500 });
      setArchivo(null); setComentario('');
      if (fileRef.current) fileRef.current.value = '';
      await cargar();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 4000 });
    } finally {
      setSubiendo(false);
    }
  };

  const handleConfirmarEliminar = async () => {
    if (!archivoAEliminar) return;
    setEliminando(true);
    try {
      await faenasApi.removeArchivo(faenaId, archivoAEliminar.id);
      toast({ title: 'Archivo eliminado', status: 'success', duration: 2000 });
      onCloseDel();
      await cargar();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setEliminando(false);
    }
  };

  const avances = faena?.avances || [];
  const archivos = faena?.archivos || [];
  const antes = archivos.filter((a) => a.momento === 'ANTES');
  const despues = archivos.filter((a) => a.momento === 'DESPUES');

  const renderThumbs = (list: any[]) => (
    <HStack spacing={2} flexWrap="wrap">
      {list.map((a) => (
        <Box key={a.id} position="relative" w="80px" h="80px">
          <Box as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank" w="80px" h="80px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200" display="block" bg="gray.100">
            <img src={`${UPLOADS_BASE}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
          <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red" borderRadius="full" p={0} minW="18px" h="18px"
            onClick={() => { setArchivoAEliminar(a); onOpenDel(); }}>
            <Icon as={FiTrash2} boxSize={2.5} />
          </Button>
        </Box>
      ))}
    </HStack>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Avances y evidencias — {faena?.folio || `Faena #${faenaId}`}
          </ModalHeader>
          <ModalBody py={5}>
            {cargando ? (
              <Text color="gray.500" textAlign="center" py={6}>Cargando...</Text>
            ) : (
              <VStack spacing={5} align="stretch">
                {/* Registrar avance */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>Registrar avance</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Nuevo estatus</FormLabel>
                      <Select placeholder="Sin cambio" value={estatusNuevo} onChange={(e) => setEstatusNuevo(e.target.value)}>
                        {ESTATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Responsable</FormLabel>
                      <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} />
                    </FormControl>
                    <FormControl gridColumn={{ md: 'span 2' }}>
                      <FormLabel fontSize="sm">Descripción</FormLabel>
                      <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
                    </FormControl>
                  </SimpleGrid>
                  <HStack justify="flex-end" mt={2}>
                    <Button size="sm" bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleAgregarAvance} isLoading={guardandoAvance} loadingText="Guardando...">
                      Agregar avance
                    </Button>
                  </HStack>
                </Box>

                <Divider />

                {/* Timeline */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>Historial de avances</Text>
                  {avances.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">Sin avances registrados</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {avances.map((av) => (
                        <Box key={av.id} borderLeft="3px solid #5b182b" pl={3} py={1}>
                          <HStack spacing={2}>
                            <Icon as={FiClock} color="#5b182b" />
                            <Text fontSize="xs" color="gray.500">{av.fecha ? new Date(av.fecha).toLocaleString('es-MX') : ''}</Text>
                            {(av.estatusAnterior || av.estatusNuevo) && (
                              <Badge colorScheme="purple" fontSize="2xs">{av.estatusAnterior || '—'} → {av.estatusNuevo || '—'}</Badge>
                            )}
                          </HStack>
                          <Text fontSize="sm" whiteSpace="pre-wrap">{av.descripcion}</Text>
                          {av.responsable && <Text fontSize="xs" color="gray.500">Responsable: {av.responsable}</Text>}
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>

                <Divider />

                {/* Evidencias */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>Evidencias</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>ANTES</Text>
                      {antes.length ? renderThumbs(antes) : <Text fontSize="xs" color="gray.400">Sin evidencias</Text>}
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>DESPUÉS</Text>
                      {despues.length ? renderThumbs(despues) : <Text fontSize="xs" color="gray.400">Sin evidencias</Text>}
                    </Box>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Momento</FormLabel>
                      <Select value={momento} onChange={(e) => setMomento(e.target.value)}>
                        <option value="ANTES">ANTES</option>
                        <option value="DESPUES">DESPUÉS</option>
                      </Select>
                    </FormControl>
                    <FormControl gridColumn={{ md: 'span 2' }}>
                      <FormLabel fontSize="sm">Comentario</FormLabel>
                      <Input value={comentario} onChange={(e) => setComentario(e.target.value)} />
                    </FormControl>
                  </SimpleGrid>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
                  <HStack mt={2} justify="space-between">
                    <Button size="sm" leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="teal" onClick={() => fileRef.current?.click()}>
                      {archivo ? archivo.name : 'Seleccionar archivo'}
                    </Button>
                    <Button size="sm" bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubirArchivo} isLoading={subiendo} loadingText="Subiendo..." isDisabled={!archivo}>
                      Subir evidencia
                    </Button>
                  </HStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenDel} leastDestructiveRef={cancelRef} onClose={onCloseDel}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar archivo</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{archivoAEliminar?.nombreArchivo}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseDel} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleConfirmarEliminar} ml={3} isLoading={eliminando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalFaenaAvances;
